/**
 * The Cortex Console System Action Log blade
 * (specs/20260918-cortex-decisions-actionable-modal/, requirements.md FR-SAL-1 to
 * FR-SAL-4, design.md sections 6 and 11).
 *
 * What this component does: render the answer `GET /api/cortex/console/system-actions`
 * gave - one read-only row per system action (starting with a Slack loop Restart
 * press), newest first: what happened, package/stream, who triggered it, when.
 *
 * What it deliberately does NOT do:
 *
 * - decide who may see a row. Eligibility was applied once by the read layer, through
 *   `StateReadRepository.system_actions()` - never `pending_founder_decisions()` /
 *   `founder_decisions()` (FR-SAL-4);
 * - render a lettered option, an "Other" action or a `may_rule` field. There is no
 *   rule control anywhere on this surface, for any caller (FR-SAL-3) - the wire shape
 *   this blade renders structurally carries neither field, so there is nothing here
 *   that could render one;
 * - invent a value for a field a row does not carry. `reported()` says "not reported"
 *   for `null`/`undefined` rather than leaving a blank or guessing (FR-SAL-2).
 */
import React from "react";
import { ContentRegionLoader } from "../components/loading";
import type {
  ConsoleRefusal,
  ConsoleSystemActionItem,
  ConsoleSystemActionsResponse,
} from "../api/types";
import styles from "./SystemActionsBlade.module.css";

/** A value the server did not report, said in words rather than left blank. */
export function reported(value: string | null | undefined): string {
  return value ? value : "not reported";
}

export function isSystemActions(
  body: ConsoleSystemActionsResponse | ConsoleRefusal,
): body is ConsoleSystemActionsResponse {
  return (
    (body as ConsoleSystemActionsResponse).response_type === "console_system_actions"
  );
}

export type SystemActionsBladeProps = {
  systemActions: ConsoleSystemActionsResponse | ConsoleRefusal | null;
  loading: boolean;
  onRefresh: () => void;
};

function MetaLine({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <p className={styles.cardLine}>
      <span className={styles.cardLabel}>{label}</span>
      <span className={styles.cardValue}>{children}</span>
    </p>
  );
}

function SystemActionCard({ row }: { row: ConsoleSystemActionItem }) {
  return (
    <article
      className={styles.card}
      data-testid={`system-action-${row.id}`}
    >
      <h4 className={styles.cardTitle}>{reported(row.what_happened)}</h4>
      <MetaLine label="Stream">{reported(row.stream)}</MetaLine>
      <MetaLine label="Package">{reported(row.package_slug)}</MetaLine>
      <MetaLine label="By">{reported(row.restarted_by)}</MetaLine>
      <MetaLine label="When">
        {reported(row.restarted_at || row.requested_at)}
      </MetaLine>
      <MetaLine label="Outcome">{reported(row.restart_outcome)}</MetaLine>
    </article>
  );
}

export function SystemActionsBlade({
  systemActions,
  loading,
  onRefresh,
}: SystemActionsBladeProps) {
  const answered =
    systemActions && isSystemActions(systemActions) ? systemActions : null;
  const message = systemActions?.message;
  // A refusal that never reached the store carries no ``system_actions`` at all
  // (undefined), never an empty list - the same rule the Instances blade applies to
  // ``shared``/``local_stacks``.
  const rows = answered?.system_actions;

  return (
    <div className={styles.blade} data-testid="console-system-actions-blade">
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>System Action Log</h2>
          {answered?.scope_statement ? (
            <p
              className={styles.scopeStatement}
              data-testid="system-actions-scope-statement"
            >
              {answered.scope_statement}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          className={styles.refresh}
          data-testid="system-actions-refresh"
          onClick={onRefresh}
        >
          Refresh
        </button>
      </div>

      {message ? (
        <p className={styles.message} data-testid="system-actions-message">
          {message}
        </p>
      ) : null}

      {loading ? (
        <ContentRegionLoader
          label="Loading System Action Log"
          data-testid="system-actions-loading"
        />
      ) : null}

      {!loading && answered && rows ? (
        <section data-testid="system-actions-list">
          {rows.length === 0 ? (
            <p className={styles.emptySection}>
              Nothing has been logged in the system action log yet.
            </p>
          ) : (
            <div className={styles.list}>
              {rows.map((row) => (
                <SystemActionCard key={row.id} row={row} />
              ))}
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}
